import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Fade,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TourIcon from '@mui/icons-material/Tour';
import { useNavigate } from 'react-router-dom';
import { useTour } from '../../context/TourContext';
import { themeBackgrounds } from '../../theme/theme';

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 8;

export default function QuickTour(): React.ReactElement | null {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isTourOpen, currentStep, steps, currentStepData, nextStep, prevStep, stopTour } = useTour();
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [cardPosition, setCardPosition] = useState<{ top: number; left: number }>({ top: 80, left: 320 });

  const measureTarget = useCallback(() => {
    if (!currentStepData) {
      setTargetRect(null);
      return;
    }

    const el = document.getElementById(currentStepData.target);
    if (!el) {
      setTargetRect(null);
      return;
    }

    el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });

    const rect = el.getBoundingClientRect();
    const measured: TargetRect = {
      top: rect.top - PADDING,
      left: rect.left - PADDING,
      width: rect.width + PADDING * 2,
      height: rect.height + PADDING * 2,
    };
    setTargetRect(measured);

    const cardWidth = 380;
    const cardHeight = 280;
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    let left = measured.left + measured.width + 16;
    let top = measured.top;

    // Prefer right of target; if not enough space, place left or below
    if (left + cardWidth > viewportW - 16) {
      left = measured.left - cardWidth - 16;
    }
    if (left < 16) {
      left = Math.max(16, Math.min(measured.left, viewportW - cardWidth - 16));
      top = measured.top + measured.height + 16;
    }
    if (top + cardHeight > viewportH - 16) {
      top = Math.max(16, viewportH - cardHeight - 16);
    }
    if (top < 16) {
      top = 16;
    }

    setCardPosition({ top, left });
  }, [currentStepData]);

  useEffect(() => {
    if (!isTourOpen || !currentStepData) return;

    // Expand sidebar and wait briefly for layout (collapse animation / nav mount)
    window.dispatchEvent(new CustomEvent('sampark-tour-expand-sidebar'));

    if (currentStepData.path) {
      navigate(currentStepData.path);
    }

    const timer = window.setTimeout(measureTarget, 350);
    window.addEventListener('resize', measureTarget);
    window.addEventListener('scroll', measureTarget, true);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', measureTarget);
      window.removeEventListener('scroll', measureTarget, true);
    };
  }, [isTourOpen, currentStepData, measureTarget, navigate, currentStep]);

  if (!isTourOpen || !currentStepData) {
    return null;
  }

  const progress = ((currentStep + 1) / steps.length) * 100;
  const isLast = currentStep === steps.length - 1;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: theme.zIndex.modal + 10,
        pointerEvents: 'none',
      }}
    >
      {/* Dim overlay with spotlight cutout via box-shadow */}
      {targetRect ? (
        <Box
          sx={{
            position: 'fixed',
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            borderRadius: 2,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.72)',
            border: `2px solid ${theme.palette.warning.main}`,
            pointerEvents: 'none',
            transition: 'all 0.3s ease',
            animation: 'tourPulse 1.8s ease-in-out infinite',
            '@keyframes tourPulse': {
              '0%, 100%': { boxShadow: `0 0 0 9999px rgba(0, 0, 0, 0.72), 0 0 0 0 rgba(255, 152, 0, 0.5)` },
              '50%': { boxShadow: `0 0 0 9999px rgba(0, 0, 0, 0.72), 0 0 0 8px rgba(255, 152, 0, 0.25)` },
            },
          }}
        />
      ) : (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.72)',
            pointerEvents: 'none',
          }}
        />
      )}

      <Fade in={isTourOpen} timeout={400}>
        <Card
          sx={{
            position: 'fixed',
            top: cardPosition.top,
            left: cardPosition.left,
            width: 380,
            maxWidth: 'calc(100vw - 32px)',
            zIndex: theme.zIndex.modal + 11,
            borderRadius: 3,
            boxShadow: theme.shadows[20],
            background: themeBackgrounds.default,
            color: 'white',
            pointerEvents: 'auto',
            transition: 'top 0.3s ease, left 0.3s ease',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TourIcon sx={{ color: theme.palette.warning.main }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                    {currentStepData.title}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Step {currentStep + 1} of {steps.length}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={stopTour}
                sx={{
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.65, opacity: 0.95 }}>
              {currentStepData.description}
            </Typography>

            <Box sx={{ mb: 2.5 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: theme.palette.warning.main,
                    borderRadius: 3,
                  },
                }}
              />
            </Box>

            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
              <Button
                size="small"
                onClick={stopTour}
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                }}
              >
                Skip Tour
              </Button>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={prevStep}
                  disabled={currentStep === 0}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.4)',
                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)', borderColor: 'rgba(255,255,255,0.15)' },
                    '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.08)' },
                  }}
                >
                  Back
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  endIcon={!isLast ? <ArrowForwardIcon /> : undefined}
                  onClick={nextStep}
                  sx={{
                    backgroundColor: theme.palette.warning.main,
                    color: 'white',
                    fontWeight: 'bold',
                    px: 2.5,
                    '&:hover': { backgroundColor: theme.palette.warning.dark },
                  }}
                >
                  {isLast ? 'Finish' : 'Next'}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Fade>
    </Box>
  );
}
